import React, { useMemo } from 'react';
import { Drawer } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSidePanelContext } from 'modules/rootPage/SidePanelContext';
import { useSubstituteUser } from 'modules/rootPage/hooks/useSubstituteUser';
import { getSmartTimesheetPanelWidth } from './SmartTimesheetPanel';

interface DrawerStyleProps {
  expanded?: boolean;
  pinned: boolean;
  isSubstituteUser: boolean;
}
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex'
  },
  drawer: {
    width: 'inherit',
    flexShrink: 0,
    backgroundColor: 'transparent'
  },
  drawerContainer: {
    marginLeft: ({ expanded, pinned }: DrawerStyleProps) =>
      expanded
        ? theme.spacing(0)
        : -`${getSmartTimesheetPanelWidth({
            pinned
          })}`,
    transition: 'margin 0.15s ease-in-out',
    width: '100%',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    height: '100%'
  },
  paperAnchorDockedLeft: {
    borderRight: 'none'
  }
}));

const useDrawerStyles = makeStyles((theme) => ({
  paper: {
    width: 'inherit',
    height: () => `calc(100% - ${theme.spacing(2)})`,
    top: ({ isSubstituteUser }: DrawerStyleProps) =>
      theme.spacing(isSubstituteUser ? 6 : 2),
    backgroundColor: 'transparent',
    overflow: 'hidden',
    paddingLeft: ({ pinned }: DrawerStyleProps) =>
      theme.spacing(pinned ? 30 : 8)
  },
  paperAnchorDockedLeft: {
    borderRight: 'none'
  }
}));

type SmartTimesheetDrawerProps = {
  children?: React.ReactNode;
  expanded?: boolean;
};

export const SmartTimesheetDrawer: React.FC<SmartTimesheetDrawerProps> = ({
  children,
  expanded
}: SmartTimesheetDrawerProps) => {
  const { isSubstituteUser } = useSubstituteUser();
  const { pinned = false } = useSidePanelContext();
  const styleProps = useMemo(() => ({ pinned, expanded, isSubstituteUser }), [
    pinned,
    expanded,
    isSubstituteUser
  ]);
  const classes = useStyles(styleProps);
  const paperClasses = useDrawerStyles(styleProps);
  return (
    <Drawer
      className={classes.drawer}
      data-testid={'smart-timesheet-drawer'}
      variant="permanent"
      classes={paperClasses}
    >
      <div
        data-testid={'smart-timesheet-drawer-container'}
        className={classes.drawerContainer}
      >
        {children}
      </div>
    </Drawer>
  );
};

export default SmartTimesheetDrawer;
